import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InventoryItemType } from 'src/app/core/enums/inventory-item-type.enum';
import { BehaviorSubject, EMPTY, catchError, finalize, first, forkJoin, tap } from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { TabItem } from 'src/app/core/models/tabs/tab-item.interface';
import { CompanySalesOrderDetails } from 'src/app/core/models/sales/company-sales-order-details.interface';
import { CompanySalesOrderStatus } from 'src/app/core/models/sales/company-sales-order-status.type';
import { SalesOrderClientOption } from 'src/app/core/models/sales/sales-order-client-option.interface';
import { SalesOrderInsufficientStockLine } from 'src/app/core/models/sales/sales-order-insufficient-stock.interface';
import { ContactService } from 'src/app/core/services/contact.service';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { OrganizationService } from 'src/app/core/services/organization.service';
import { SalesOrderService } from 'src/app/core/services/sales-order.service';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import {
  buildContactClientOption,
  buildOrganizationClientOption,
} from '../sales-order-client-search.util';
import { buildInventoryItemOption } from 'src/app/core/utils/inventory-item-search.util';
import { getSalesOrderStatusLabelKey, normalizeSalesOrderStatus } from '../sales-order-status-label.util';
import { SalesOrderFinanceTabComponent } from './sales-order-finance-tab/sales-order-finance-tab.component';
import { SalesOrderHistoryTabComponent } from './sales-order-history-tab/sales-order-history-tab.component';
import { SalesOrderIntegrationTabComponent } from './sales-order-integration-tab/sales-order-integration-tab.component';
import { SalesOrderLinesTabComponent } from './sales-order-lines-tab/sales-order-lines-tab.component';
import { SalesOrderCreateInvoiceModalComponent } from './sales-order-invoices-tab/sales-order-create-invoice-modal/sales-order-create-invoice-modal.component';
import {
  SalesOrderHeroIconView,
  SalesOrderHeroView,
  SalesOrderIntegrationView,
  SalesOrderInventoryItemOption,
  SalesOrderTab,
  SalesOrderTypeBadgeView,
} from './sales-order-overview.models';
import { SalesOrderOverviewTabComponent } from './sales-order-overview-tab/sales-order-overview-tab.component';
import { SalesOrderShipmentsTabComponent } from './sales-order-shipments-tab/sales-order-shipments-tab.component';
import { SalesOrderCreateShipmentModalComponent } from './sales-order-shipments-tab/sales-order-create-shipment-modal/sales-order-create-shipment-modal.component';

const INVENTORY_ITEM_LIST_PAGE_SIZE = 200;

@Component({
  selector: 'app-sales-order-overview',
  standalone: true,
  imports: [
    CommonModule,
    CoreModule,
    TranslateModule,
    SalesOrderCreateInvoiceModalComponent,
    SalesOrderCreateShipmentModalComponent,
    SalesOrderFinanceTabComponent,
    SalesOrderHistoryTabComponent,
    SalesOrderIntegrationTabComponent,
    SalesOrderLinesTabComponent,
    SalesOrderOverviewTabComponent,
    SalesOrderShipmentsTabComponent,
  ],
  templateUrl: './sales-order-overview.component.html',
  styleUrl: './sales-order-overview.component.scss',
})
export class SalesOrderOverviewComponent {
  private readonly salesOrderService = inject(SalesOrderService);
  private readonly inventoryItemService = inject(InventoryItemService);
  private readonly organizationService = inject(OrganizationService);
  private readonly contactService = inject(ContactService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);

  public readonly salesOrder$ = new BehaviorSubject<CompanySalesOrderDetails | null>(null);
  public readonly unitsOfMeasure$ = new BehaviorSubject<UnitOfMeasureLookup[]>([]);
  public readonly clientOptions$ = new BehaviorSubject<SalesOrderClientOption[]>([]);
  public readonly inventoryItemOptions$ = new BehaviorSubject<SalesOrderInventoryItemOption[]>([]);
  public readonly heroView$ = new BehaviorSubject<SalesOrderHeroView | null>(null);
  public readonly integrationView$ = new BehaviorSubject<SalesOrderIntegrationView | null>(null);
  public readonly typeBadge$ = new BehaviorSubject<SalesOrderTypeBadgeView | null>(null);
  public readonly heroIcon$ = new BehaviorSubject<SalesOrderHeroIconView>({
    gradientClass: 'sales-order-overview-page__hero-icon-block--neutral',
    iconKey: 'neutral',
  });

  public salesOrderHeaderTitle = '';
  public salesOrderSubtitle: string | null = null;

  // Note: the 'integration' tab is intentionally absent from `allTabs` — it is hidden from the
  // tabs strip for now. The underlying `SalesOrderIntegrationTabComponent`, the `'integration'`
  // member of the `SalesOrderTab` union, and the `@case ('integration')` branch in the template
  // are kept in place so the surface can be re-enabled without re-plumbing it.
  public readonly allTabs: TabItem[] = [
    { id: 'overview', labelKey: 'COMPONENTS.SALES_ORDERS.TABS.OVERVIEW', icon: 'file-text' },
    { id: 'lines', labelKey: 'COMPONENTS.SALES_ORDERS.TABS.LINES', icon: 'shopping-cart' },
    { id: 'shipments', labelKey: 'COMPONENTS.SALES_ORDERS.TABS.SHIPMENTS', icon: 'check-circle' },
    { id: 'finance', labelKey: 'COMPONENTS.SALES_ORDERS.TABS.FINANCE', icon: 'credit-card' },
    { id: 'history', labelKey: 'COMPONENTS.SALES_ORDERS.TABS.HISTORY', icon: 'clock' },
  ];

  // Shipments tab is always visible — the user can create a shipment from any sales-order
  // status, and read-only history remains useful in terminal states. Invoices live inside
  // the Finance tab now (merged from the standalone Invoices tab).
  public tabs: TabItem[] = this.allTabs;

  public readonly statusOptions: { value: CompanySalesOrderStatus; labelKey: string }[] = (
    ['Draft', 'New', 'Confirmed', 'Cancelled'] as CompanySalesOrderStatus[]
  ).map((value) => ({ value, labelKey: getSalesOrderStatusLabelKey(value) }));

  public readonly insufficientStockLines$ = new BehaviorSubject<SalesOrderInsufficientStockLine[]>([]);

  public companyId = '';
  public salesOrderId = '';
  public salesOrdersBackUrl = '/';
  // Incremented every time the underlying order is re-applied so the History tab knows to refetch.
  public historyRefreshToken = 0;
  // Mirrors the History tab pattern for the Finance + Shipments tabs. Bumped whenever the
  // parent re-fetches the order (e.g. after a quick-action mutation from the Overview tab)
  // so the dependent tab invalidates its cached list and re-loads when it becomes visible.
  public financeRefreshToken = 0;
  public shipmentsRefreshToken = 0;
  // Resolved human-readable customer name for the active sales order (organization title or
  // contact full name). Computed once per `applySalesOrder` from `clientOptions$` so child tabs
  // and modals can display the actual customer instead of the BC-style customerNumber.
  public customerName = '';
  public isLoading = false;
  public pageErrorKey = '';
  public activeTab: SalesOrderTab = 'overview';
  public statusLabelKey = '';
  public statusBadgeKey: 'draft' | 'new' | 'confirmed' | 'cancelled' = 'draft';
  public isArchived = false;
  public canDelete = false;
  public isTerminalState = false;
  public terminalBannerKey: string | null = null;
  public isHeroActionInFlight = false;
  public isConfirmationOpen = false;
  public confirmationVariant: 'archive' | 'delete' | null = null;
  public confirmationTitleKey = '';
  public confirmationPromptKey = '';
  public confirmationActionKey = '';
  public confirmationLoadingKey = '';
  public heroActionErrorKey = '';
  // Single create-invoice / create-shipment modal mounts on the parent overview that serve
  // every entry point — the Overview tab's quick actions AND the in-tab "+ NEW" buttons on
  // the Finance / Shipments tabs (which emit `createInvoiceRequested` / `createShipmentRequested`
  // outputs the parent funnels back through these flags). Lifting the modal to a single
  // mount eliminates the visual drift the previous dual-mount design exhibited and removes
  // the cross-tab pendingAction signalling that previously caused NG0100.
  public isCreateInvoiceModalOpen = false;
  public isCreateShipmentModalOpen = false;
  // Pending `quickAction` query-param picked up from the URL when the user lands here from
  // a row action on the sales-orders list. Held until the order finishes loading and the
  // requested modal opens (or is gated out by the existing guards), at which point the
  // param is stripped from the URL via the router so a refresh does not retrigger it.
  private pendingQuickAction: 'invoice' | 'shipment' | null = null;
  private route!: ActivatedRoute;
  // Duplicate-order flow lives entirely on the parent — no child interaction.
  public isDuplicating = false;
  public duplicateErrorKey = '';

  constructor() {
    const route = inject(ActivatedRoute);
    this.route = route;

    this.companyId = route.parent?.parent?.snapshot.params['id'] ?? '';
    this.salesOrdersBackUrl = `/companies/${this.companyId}/crm/sales-orders`;

    // Subscribe for the component lifetime, not just the first emission. When the user
    // duplicates an order, Router navigates from /sales-orders/{oldId} to /sales-orders/{newId};
    // Angular's default RouteReuseStrategy reuses the same SalesOrderOverviewComponent instance,
    // so the constructor does not run again. Reacting to every params emission (de-duplicated by
    // id) lets the page re-load when the URL changes on the same route.
    route.params
      .pipe(
        takeUntilDestroyed(),
        tap((params) => {
          const nextSalesOrderId = params['salesOrderId'];
          if (!nextSalesOrderId || nextSalesOrderId === this.salesOrderId) {
            return;
          }
          this.salesOrderId = nextSalesOrderId;
          this.loadPage();
        }),
      )
      .subscribe();

    // Pick up the quickAction query-param hand-off from the sales-orders list row actions
    // (`?quickAction=invoice|shipment`). Stored as a pending intent; consumed once
    // `applySalesOrder` lands so the modal opens with a fully loaded order.
    route.queryParams
      .pipe(
        takeUntilDestroyed(),
        tap((params) => {
          const value = params['quickAction'];
          if (value === 'invoice' || value === 'shipment') {
            this.pendingQuickAction = value;
            this.consumePendingQuickAction();
          }
        }),
      )
      .subscribe();
  }

  public onTabChange(tabId: string): void {
    // The standalone Invoices tab was merged into Finance. Redirect any legacy deep links
    // or external navigations that still target 'invoices' to 'finance' so users land on the
    // right surface instead of a dead tab id.
    if (tabId === 'invoices') {
      this.activeTab = 'finance';
      return;
    }
    // The Integration tab is hidden from the tabs strip; coerce any deep-link / persisted
    // state pointing at it back to the overview so the user never lands on an invisible tab.
    if (tabId === 'integration') {
      this.activeTab = 'overview';
      return;
    }
    this.activeTab = tabId as SalesOrderTab;
  }

  public onOrderUpdated(order: CompanySalesOrderDetails): void {
    this.applySalesOrder(order);
  }

  public onLinesOrderUpdated(order: CompanySalesOrderDetails): void {
    this.applySalesOrder(order);
    this.activeTab = 'lines';
  }

  public onShipmentCreated(): void {
    if (!this.companyId || !this.salesOrderId) {
      return;
    }
    // Re-fetch the sales order so totals and per-line shippedQuantity are refreshed
    // everywhere. The shipments tab refetches its own list.
    this.salesOrderService
      .getSalesOrder(this.companyId, this.salesOrderId)
      .pipe(
        first(),
        tap((order) => this.applySalesOrder(order)),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  public onInvoiceCreated(): void {
    if (!this.companyId || !this.salesOrderId) {
      return;
    }
    // Re-fetch the sales order so totals and per-line invoicedQuantity are refreshed
    // everywhere. The invoices tab refetches its own list.
    this.salesOrderService
      .getSalesOrder(this.companyId, this.salesOrderId)
      .pipe(
        first(),
        tap((order) => this.applySalesOrder(order)),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  public onInsufficientStock(lines: SalesOrderInsufficientStockLine[]): void {
    this.insufficientStockLines$.next(lines);
    this.activeTab = 'lines';
  }

  // Funnel for every "create invoice" / "create shipment" entry point — the Overview tab's
  // quick actions AND the Finance / Shipments tabs' in-tab "+ NEW" buttons. Opens the single
  // parent-mounted modal directly so the user stays on the active tab. Defense in depth:
  // also guard against terminal / archived orders even though the originating CTAs gate the
  // click via their own canCreate / disabled flags.
  public onIssueInvoiceRequested(): void {
    const order = this.salesOrder$.getValue();
    if (!order || order.status === 'Cancelled' || order.isArchived === true) {
      return;
    }
    this.isCreateInvoiceModalOpen = true;
  }

  public onCreateShipmentRequested(): void {
    const order = this.salesOrder$.getValue();
    if (!order || order.status === 'Cancelled' || order.isArchived === true) {
      return;
    }
    this.isCreateShipmentModalOpen = true;
  }

  public closeCreateInvoiceModal(): void {
    this.isCreateInvoiceModalOpen = false;
  }

  // Wired to the single parent-mounted create-invoice modal's `invoiceCreated` output.
  // Closes the modal, re-fetches the sales order (existing `onInvoiceCreated` handler) and
  // bumps the Finance tab's `refreshToken` so the cached invoice list reloads when the user
  // is on the Finance tab (or later switches to it).
  public onInvoiceCreatedFromQuickAction(): void {
    this.isCreateInvoiceModalOpen = false;
    this.onInvoiceCreated();
    this.financeRefreshToken += 1;
  }

  public closeCreateShipmentModal(): void {
    this.isCreateShipmentModalOpen = false;
  }

  // Wired to the single parent-mounted create-shipment modal's `shipmentCreated` output.
  // Same flow as the invoice handler but targeting the Shipments tab's `refreshToken`.
  public onShipmentCreatedFromQuickAction(): void {
    this.isCreateShipmentModalOpen = false;
    this.onShipmentCreated();
    this.shipmentsRefreshToken += 1;
  }

  public onDuplicateOrderRequested(): void {
    if (this.isDuplicating || !this.companyId || !this.salesOrderId) {
      return;
    }
    this.isDuplicating = true;
    this.duplicateErrorKey = '';
    // Clear any previous hero error so the existing error surface only shows the duplicate
    // error while it is relevant. Re-using `heroActionErrorKey` keeps the page to a single
    // error surface (per spec) instead of introducing a second banner.
    this.heroActionErrorKey = '';

    this.salesOrderService
      .duplicateSalesOrder(this.companyId, this.salesOrderId)
      .pipe(
        first(),
        tap((newOrder) => {
          this.router.navigate([`${this.salesOrdersBackUrl}/${newOrder.id}`]);
        }),
        catchError(() => {
          this.duplicateErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.DUPLICATE_FAILED';
          this.heroActionErrorKey = this.duplicateErrorKey;
          return EMPTY;
        }),
        finalize(() => {
          this.isDuplicating = false;
        }),
      )
      .subscribe();
  }

  public retry(): void {
    this.loadPage();
  }

  public openArchiveConfirmation(): void {
    if (this.isHeroActionInFlight || this.isArchived) {
      return;
    }
    this.heroActionErrorKey = '';
    this.confirmationVariant = 'archive';
    this.confirmationTitleKey = 'COMPONENTS.SALES_ORDERS.MODAL.ARCHIVE_TITLE';
    this.confirmationPromptKey = 'COMPONENTS.SALES_ORDERS.MODAL.ARCHIVE_PROMPT';
    this.confirmationActionKey = 'COMPONENTS.SALES_ORDERS.BUTTONS.ARCHIVE_ORDER';
    this.confirmationLoadingKey = 'COMPONENTS.SALES_ORDERS.BUTTONS.ARCHIVING';
    this.isConfirmationOpen = true;
  }

  public unarchiveOrder(): void {
    if (this.isHeroActionInFlight || !this.isArchived) {
      return;
    }
    this.heroActionErrorKey = '';
    this.isHeroActionInFlight = true;

    this.salesOrderService
      .unarchiveSalesOrder(this.companyId, this.salesOrderId)
      .pipe(
        first(),
        tap((order) => this.applySalesOrder(order)),
        catchError(() => {
          this.heroActionErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.UNARCHIVE_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isHeroActionInFlight = false;
        }),
      )
      .subscribe();
  }

  public openDeleteConfirmation(): void {
    if (this.isHeroActionInFlight || !this.canDelete) {
      return;
    }
    this.heroActionErrorKey = '';
    this.confirmationVariant = 'delete';
    this.confirmationTitleKey = 'COMPONENTS.SALES_ORDERS.MODAL.DELETE_TITLE';
    this.confirmationPromptKey = 'COMPONENTS.SALES_ORDERS.MODAL.DELETE_PROMPT';
    this.confirmationActionKey = 'COMPONENTS.SALES_ORDERS.BUTTONS.DELETE_ORDER';
    this.confirmationLoadingKey = 'COMPONENTS.SALES_ORDERS.BUTTONS.DELETING';
    this.isConfirmationOpen = true;
  }

  public closeHeroConfirmation(): void {
    if (this.isHeroActionInFlight) {
      return;
    }
    this.isConfirmationOpen = false;
    this.confirmationVariant = null;
  }

  public confirmHeroAction(): void {
    if (this.isHeroActionInFlight || !this.confirmationVariant) {
      return;
    }
    if (this.confirmationVariant === 'archive') {
      this.executeArchive();
      return;
    }
    if (this.confirmationVariant === 'delete') {
      this.executeDelete();
    }
  }

  private executeArchive(): void {
    this.isHeroActionInFlight = true;
    this.salesOrderService
      .archiveSalesOrder(this.companyId, this.salesOrderId)
      .pipe(
        first(),
        tap(() => this.router.navigate([this.salesOrdersBackUrl])),
        catchError(() => {
          this.heroActionErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.ARCHIVE_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isHeroActionInFlight = false;
          this.isConfirmationOpen = false;
          this.confirmationVariant = null;
        }),
      )
      .subscribe();
  }

  private executeDelete(): void {
    this.isHeroActionInFlight = true;
    this.salesOrderService
      .deleteSalesOrder(this.companyId, this.salesOrderId)
      .pipe(
        first(),
        tap(() => this.router.navigate([this.salesOrdersBackUrl])),
        catchError(() => {
          this.heroActionErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.DELETE_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isHeroActionInFlight = false;
          this.isConfirmationOpen = false;
          this.confirmationVariant = null;
        }),
      )
      .subscribe();
  }

  private loadPage(): void {
    if (!this.companyId || !this.salesOrderId) {
      return;
    }

    this.isLoading = true;
    this.pageErrorKey = '';

    this.organizationService.setOrganizationsUrl(this.companyId);
    this.contactService.setContactsUrl(this.companyId);

    forkJoin({
      order: this.salesOrderService.getSalesOrder(this.companyId, this.salesOrderId),
      units: this.inventoryItemService.getUnitsOfMeasureLookup(this.companyId),
      organizations: this.organizationService.getCompanyOrganizations(),
      contacts: this.contactService.loadCompanyContacts(this.companyId),
      inventoryItems: this.inventoryItemService.getCompanyInventoryItemsPaged(this.companyId, {
        filters: [],
        page: 1,
        pageSize: INVENTORY_ITEM_LIST_PAGE_SIZE,
      }),
    })
      .pipe(
        first(),
        tap(({ order, units, organizations, contacts, inventoryItems }) => {
          this.unitsOfMeasure$.next(units);
          this.clientOptions$.next([
            ...organizations.map(buildOrganizationClientOption),
            ...contacts.map(buildContactClientOption),
          ]);
          this.inventoryItemOptions$.next(inventoryItems.items.map(buildInventoryItemOption));
          this.applySalesOrder(order);
        }),
        catchError(() => {
          this.pageErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.LOAD_DETAILS_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private applySalesOrder(order: CompanySalesOrderDetails): void {
    const status = normalizeSalesOrderStatus(order.status);

    this.salesOrder$.next(order);
    this.statusLabelKey = getSalesOrderStatusLabelKey(status);
    this.statusBadgeKey = status.toLowerCase() as typeof this.statusBadgeKey;
    this.isArchived = !!order.isArchived;
    this.canDelete = status === 'Draft' && !this.isArchived;
    this.isTerminalState = status === 'Cancelled';
    this.terminalBannerKey = status === 'Cancelled'
      ? 'COMPONENTS.SALES_ORDERS.MESSAGES.CANCELLED_BANNER'
      : null;
    this.tabs = this.allTabs;
    if (status !== 'Draft' && status !== 'New') {
      this.insufficientStockLines$.next([]);
    }
    this.customerName = this.resolveCustomerName(order);
    this.heroView$.next({
      createdAt: order.createdAt ?? null,
      updatedAt: order.lastModifiedDateTime ?? null,
      amountIncludingTax: order.totals.amountIncludingTax,
    });
    this.integrationView$.next(this.buildIntegrationView(order));
    this.typeBadge$.next(this.buildTypeBadge(order.type ?? null));
    this.heroIcon$.next(this.buildHeroIcon(order.type ?? null));
    this.salesOrderHeaderTitle = this.translateService.instant(
      'COMPONENTS.SALES_ORDERS.OVERVIEW_TITLE',
      { number: order.number ?? '—' },
    );
    this.salesOrderSubtitle = order.shortDescription?.trim() || null;
    this.historyRefreshToken += 1;
    // Mirror the History tab pattern: any time the parent re-fetches the order, push fresh
    // invalidation tokens to the Finance + Shipments tabs so they reload their cached lists.
    this.financeRefreshToken += 1;
    this.shipmentsRefreshToken += 1;

    // Drain any pending quickAction handed off via the URL query param now that the order
    // is fully applied and the existing guards (onIssueInvoiceRequested / onCreateShipmentRequested)
    // can evaluate against real status / isArchived data.
    this.consumePendingQuickAction();
  }

  private consumePendingQuickAction(): void {
    if (!this.pendingQuickAction || !this.salesOrder$.getValue()) {
      return;
    }
    const action = this.pendingQuickAction;
    this.pendingQuickAction = null;
    if (action === 'invoice') {
      this.onIssueInvoiceRequested();
    } else if (action === 'shipment') {
      this.onCreateShipmentRequested();
    }
    // Strip the consumed query param so a manual refresh does not retrigger the modal.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { quickAction: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private buildTypeBadge(type: InventoryItemType | null): SalesOrderTypeBadgeView | null {
    if (!type) {
      return null;
    }

    const variantMap: Record<InventoryItemType, SalesOrderTypeBadgeView['variant']> = {
      Product: 'product',
      Service: 'service',
      Hybrid: 'hybrid',
    };

    const labelKeyMap: Record<InventoryItemType, string> = {
      Product: 'COMPONENTS.SALES_ORDERS.TYPES.PRODUCT',
      Service: 'COMPONENTS.SALES_ORDERS.TYPES.SERVICE',
      Hybrid: 'COMPONENTS.SALES_ORDERS.TYPES.HYBRID',
    };

    return { type, variant: variantMap[type], labelKey: labelKeyMap[type] };
  }

  // Precomputed view-model for the hero card type icon. Returns the matching tailwind
  // gradient class + the icon-key the template uses to pick which SVG to render. The
  // neutral variant is used while the order has no line type yet (Draft / empty-lines).
  private buildHeroIcon(type: InventoryItemType | null): SalesOrderHeroIconView {
    const iconKeyMap: Record<InventoryItemType, SalesOrderHeroIconView['iconKey']> = {
      Product: 'product',
      Service: 'service',
      Hybrid: 'hybrid',
    };

    const gradientMap: Record<SalesOrderHeroIconView['iconKey'], string> = {
      product: 'sales-order-overview-page__hero-icon-block--product',
      service: 'sales-order-overview-page__hero-icon-block--service',
      hybrid: 'sales-order-overview-page__hero-icon-block--hybrid',
      neutral: 'sales-order-overview-page__hero-icon-block--neutral',
    };

    const iconKey: SalesOrderHeroIconView['iconKey'] = type ? iconKeyMap[type] : 'neutral';
    return { gradientClass: gradientMap[iconKey], iconKey };
  }

  // Look up the resolved organization/contact name from the loaded clientOptions. The order may
  // reference either an organization (customerOrganizationId) or a contact (customerContactId);
  // legacy BC-imported rows may only carry customerNumber. We fall back to "" so the consumer can
  // decide its own placeholder (the modal falls back to customerNumber → "—").
  private resolveCustomerName(order: CompanySalesOrderDetails): string {
    const options = this.clientOptions$.getValue();
    const match = options.find((opt) => {
      if (opt.type === 'organization' && order.customerOrganizationId) {
        return opt.entityId === order.customerOrganizationId;
      }
      if (opt.type === 'contact' && order.customerContactId) {
        return opt.entityId === order.customerContactId;
      }
      return false;
    });
    return match?.title ?? '';
  }

  private buildIntegrationView(order: CompanySalesOrderDetails): SalesOrderIntegrationView {
    const isSynced = !!order.businessCentralId;
    return {
      isSynced,
      sourceBadgeVariant: isSynced ? 'bc' : 'local',
      sourceLabelKey: isSynced
        ? 'COMPONENTS.SALES_ORDERS.INTEGRATION.SOURCE_BUSINESS_CENTRAL'
        : 'COMPONENTS.SALES_ORDERS.INTEGRATION.SOURCE_NATIVE',
      sourceBadgeKey: isSynced
        ? 'COMPONENTS.SALES_ORDERS.INTEGRATION.BADGE_SYNCED'
        : 'COMPONENTS.SALES_ORDERS.INTEGRATION.BADGE_NATIVE',
      businessCentralId: order.businessCentralId ?? null,
      lastModified: order.lastModifiedDateTime ?? null,
    };
  }
}
